if (!customElements.get('axiometa-story-videos')) {
  customElements.define('axiometa-story-videos', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videos = [...this.querySelectorAll('video')];
      this.visible = new Set();
      const shouldPlay = (video) => this.isConnected && this.visible.has(video) && !document.hidden && !this.motion.matches;
      const update = () => {
        this.videos.forEach((video) => {
          if (!shouldPlay(video)) {
            video.pause();
          } else if (video.paused) {
            video.play().then(() => {
              if (!signal.aborted && !shouldPlay(video)) video.pause();
            }).catch(() => {
              // Keep the poster if the browser disallows automatic playback.
            });
          }
        });
      };
      this.videos.forEach((video) => {
        video.controls = false;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
      });
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting && intersectionRatio >= .15) this.visible.add(target);
          else this.visible.delete(target);
        });
        update();
      }, { threshold: [0, .15] });
      this.videos.forEach((video) => this.observer.observe(video));
      document.addEventListener('visibilitychange', update, { signal });
      this.motion.addEventListener('change', update, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.visible?.clear();
      this.videos?.forEach((video) => video.pause());
    }
  });
}

if (!customElements.get('axiometa-story-hero')) {
  customElements.define('axiometa-story-hero', class extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.wia__header');
      this.stage = this.querySelector('.wia-hero__stage');
      this.viewer = this.querySelector('.wia-hero__model');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.update = () => {
        this.frame = null;
        // Start the fade after the introduction has passed the sticky navigation.
        const distance = Math.max((this.stage?.clientHeight || 300) * 1.25, 320);
        const progress = this.motion.matches ? 0 : Math.min(1, Math.max(0, (96 - this.header.getBoundingClientRect().bottom) / distance));
        const fade = progress * progress * (3 - 2 * progress);
        const value = fade.toFixed(3);
        if (value !== this.lastFade) {
          this.style.setProperty('--wia-hero-fade', value);
          this.lastFade = value;
        }
      };
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(this.update);
      };
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      this.motion.addEventListener('change', this.schedule, { signal });
      this.setupRotation(signal);

      if (this.viewer) {
        const updateCamera = () => {
          // Derive the center from the current GLB, not the previous CAD export.
          this.viewer.setAttribute('camera-target', 'auto auto auto');
          this.viewer.setAttribute('camera-orbit', '-20deg 55deg 110%');
          this.viewer.jumpCameraToGoal?.();
        };
        updateCamera();
        const loaded = () => {
          updateCamera();
          this.stage.classList.add('is-loaded');
          this.classList.add('is-board-ready');
          this.stage.classList.remove('is-unavailable');
          this.querySelector('.wia-hero__hint').hidden = false;
          this.updateRotation();
        };
        const unavailable = () => {
          this.stage.classList.remove('is-loaded');
          this.classList.remove('is-board-ready', 'is-board-hover');
          this.endBoardDrag();
          this.stage.classList.add('is-unavailable');
          this.querySelector('.wia-hero__hint').hidden = true;
        };
        this.viewer.addEventListener('load', loaded, { signal });
        this.viewer.addEventListener('error', unavailable, { signal });
        if (this.viewer.loaded) loaded();
        this.setupBoardInteraction(signal);
      }
      this.setupModules(signal);
      this.update();
    }

    setupBoardInteraction(signal) {
      // Oversized boards can extend over the article's following sections.
      const interactionRoot = this.closest('.wia') || this;
      const hitBoard = (event) => {
        if (!this.viewer.loaded || !this.classList.contains('is-board-ready')) return false;
        const box = this.viewer.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) return false;
        return Boolean(this.viewer.positionAndNormalFromPoint?.(event.clientX, event.clientY));
      };
      // A large transparent canvas sits above the collage. Hit-test the board
      // itself so its empty corners never steal the surrounding module controls.
      interactionRoot.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || !hitBoard(event)) return;
        event.stopPropagation();
        if (event.pointerType === 'mouse') event.preventDefault();
        this.releaseModule();
        this.boardDrag = {
          id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY,
          orbit: this.viewer.getCameraOrbit(), width: this.viewer.clientWidth,
        };
        this.setPointerCapture(event.pointerId);
        this.classList.add('is-board-dragging');
        this.viewer.shadowRoot?.querySelector('.userInput')?.focus({ preventScroll: true });
        this.boardFocused = false;
        this.updateRotation();
      }, { capture: true, signal });
      interactionRoot.addEventListener('pointermove', (event) => {
        const drag = this.boardDrag;
        if (!drag) {
          if (event.pointerType === 'mouse' && !event.buttons) {
            const hovered = hitBoard(event);
            if (hovered !== this.classList.contains('is-board-hover')) {
              this.classList.toggle('is-board-hover', hovered);
              this.updateRotation();
            }
          }
          return;
        }
        if (drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (drag.type === 'touch' && !drag.intent) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          drag.intent = Math.abs(dx) > Math.abs(dy) ? 'rotate' : 'scroll';
        }
        if (drag.intent === 'scroll') return;
        event.stopPropagation();
        const scale = Math.PI * 2 / drag.width;
        const theta = drag.orbit.theta - dx * scale;
        const phi = Math.max(.01, Math.min(Math.PI - .01, drag.orbit.phi - dy * scale));
        this.viewer.setAttribute('camera-orbit', `${theta}rad ${phi}rad 110%`);
        this.viewer.jumpCameraToGoal?.();
      }, { capture: true, signal });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
        this.addEventListener(type, (event) => {
          if (this.boardDrag?.id === event.pointerId) this.endBoardDrag();
        }, { signal });
      });
      interactionRoot.addEventListener('pointerleave', () => {
        this.classList.remove('is-board-hover');
        this.updateRotation();
      }, { signal });
      this.viewer.addEventListener('focusin', () => {
        this.boardFocused = Boolean(this.viewer.shadowRoot?.activeElement?.matches(':focus-visible'));
        this.updateRotation();
      }, { signal });
      this.viewer.addEventListener('keydown', () => {
        this.boardFocused = true;
        this.updateRotation();
      }, { signal });
      this.viewer.addEventListener('focusout', () => queueMicrotask(() => {
        if (signal.aborted) return;
        this.boardFocused = Boolean(this.viewer.shadowRoot?.activeElement?.matches(':focus-visible'));
        this.updateRotation();
      }), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.endBoardDrag();
      }, { signal });
    }

    endBoardDrag() {
      const drag = this.boardDrag;
      this.boardDrag = null;
      this.classList.remove('is-board-dragging');
      if (drag && this.hasPointerCapture(drag.id)) this.releasePointerCapture(drag.id);
      this.updateRotation();
    }

    setupRotation(signal) {
      this.rotationButton = this.querySelector('.wia-hero__rotation');
      this.rotationPaused = this.motion.matches;
      this.stageVisible = false;
      this.boardVisible = false;
      this.boardFocused = false;
      if (this.rotationButton) {
        this.rotationButton.hidden = !this.querySelector('.wia-orbit') && !(parseFloat(this.viewer?.getAttribute('rotation-per-second')) > 0);
        this.rotationButton.addEventListener('click', () => {
          this.rotationPaused = !this.rotationPaused;
          this.updateRotation();
        }, { signal });
      }
      this.motion.addEventListener('change', () => {
        this.rotationPaused = this.motion.matches;
        this.updateRotation();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.releaseModule();
        this.updateRotation();
        this.pumpModules();
      }, { signal });
      if (this.stage) {
        this.rotationObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.stage) this.stageVisible = entry.isIntersecting;
            if (entry.target === this.viewer) this.boardVisible = entry.isIntersecting;
          });
          if (!this.stageVisible && !this.boardVisible) this.releaseModule();
          this.updateRotation();
          this.pumpModules();
        });
        this.rotationObserver.observe(this.stage);
        if (this.viewer) this.rotationObserver.observe(this.viewer);
      }
      this.updateRotation();
    }

    setupModules(signal) {
      this.orbit = this.querySelector('.wia-orbit');
      if (!this.orbit) return;
      this.moduleStatus = this.querySelector('.wia-orbit__status');
      this.sdkReady = false;
      this.modulesLoading = 0;
      this.modules = [...this.orbit.querySelectorAll('.wia-orbit__trigger')].map((trigger, index) => {
        const [theta, phi, radius] = (trigger.dataset.cameraOrbit || '-25deg 60deg 110%').split(/\s+/);
        const entry = {
          trigger, wrapper: trigger.closest('.wia-orbit__module'), index, state: 'queued',
          pose: { theta: parseFloat(theta), phi: parseFloat(phi), radius },
        };
        entry.wrapper.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          entry.hovered = true;
          this.prioritizeModule(entry);
          this.updateRotation();
        }, { signal });
        entry.wrapper.addEventListener('pointerleave', () => {
          entry.hovered = false;
          this.updateRotation();
        }, { signal });
        entry.wrapper.addEventListener('focusin', () => {
          entry.focused = true;
          this.updateRotation();
        }, { signal });
        entry.wrapper.addEventListener('focusout', () => queueMicrotask(() => {
          if (signal.aborted) return;
          entry.focused = entry.wrapper.contains(document.activeElement);
          if (!entry.focused && this.activeModule === entry) this.releaseModule();
          this.updateRotation();
        }), { signal });
        return entry;
      });
      this.moduleQueue = [...this.modules];

      this.orbit.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        const entry = this.moduleFor(event.target);
        if (!entry) return;
        this.selectModule(entry);
        // Once ready, model-viewer's native controls own the gesture.
        if (!event.target.closest('.wia-orbit__trigger')) {
          this.nativeDragging = true;
          this.updateRotation();
          return;
        }
        entry.trigger.focus({ preventScroll: true });
        this.moduleDrag = {
          id: event.pointerId, type: event.pointerType, entry,
          x: event.clientX, y: event.clientY, pose: { ...entry.pose },
        };
        entry.wrapper.setPointerCapture(event.pointerId);
        entry.wrapper.classList.add('is-dragging');
        if (event.pointerType === 'mouse') event.preventDefault();
        this.updateRotation();
      }, { signal });
      this.orbit.addEventListener('pointermove', (event) => {
        const drag = this.moduleDrag;
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        // Vertical touch gestures remain native page scrolling.
        if (drag.type === 'touch' && !drag.intent) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          drag.intent = Math.abs(dx) > Math.abs(dy) ? 'rotate' : 'scroll';
        }
        if (drag.intent === 'scroll') return;
        const sensitivity = 180 / drag.entry.wrapper.clientWidth;
        drag.entry.pose = {
          theta: drag.pose.theta - dx * sensitivity,
          phi: Math.max(5, Math.min(175, drag.pose.phi - dy * sensitivity)),
          radius: drag.pose.radius,
        };
        this.applyModulePose(drag.entry);
      }, { signal });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
        window.addEventListener(type, (event) => {
          if (this.nativeDragging || this.moduleDrag?.id === event.pointerId) this.endModuleDrag();
        }, { signal });
      });
      this.orbit.addEventListener('click', (event) => {
        if (!event.target.closest('.wia-orbit__trigger')) return;
        const entry = this.moduleFor(event.target);
        if (entry) this.selectModule(entry, event.detail === 0);
      }, { signal });
      this.orbit.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.releaseModule();
        this.rotationButton.focus({ preventScroll: true });
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (this.activeModule && !this.activeModule.wrapper.contains(event.target)) this.releaseModule();
      }, { signal });

      // Give the Mini first priority, then prepare two real models at a time.
      // Ready viewers stay mounted: selecting or returning never reloads a GLB.
      const boardSettled = () => {
        this.boardSettled = true;
        clearTimeout(this.boardTimeout);
        this.pumpModules();
      };
      this.viewer?.addEventListener('load', boardSettled, { once: true, signal });
      this.viewer?.addEventListener('error', boardSettled, { once: true, signal });
      this.boardSettled = !this.viewer || this.viewer.loaded || this.stage.classList.contains('is-unavailable');
      this.boardTimeout = setTimeout(boardSettled, 8000);
      customElements.whenDefined('model-viewer').then(() => {
        if (signal.aborted) return;
        this.sdkReady = true;
        this.pumpModules();
      });
      this.updateRotation();
    }

    moduleFor(target) {
      const wrapper = target.closest('.wia-orbit__module');
      return this.modules.find((entry) => entry.wrapper === wrapper);
    }

    prioritizeModule(entry) {
      if (entry.state !== 'queued') return;
      this.moduleQueue = [entry, ...this.moduleQueue.filter((item) => item !== entry)];
      this.pumpModules();
    }

    pumpModules() {
      if (!this.isConnected || !this.sdkReady || !this.boardSettled || !this.stageVisible || document.hidden) return;
      while (this.modulesLoading < 2 && this.moduleQueue.length) this.loadModule(this.moduleQueue.shift());
    }

    loadModule(entry) {
      if (entry.state !== 'queued') return;
      entry.state = 'loading';
      this.modulesLoading++;
      const { wrapper, trigger } = entry;
      wrapper.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
      const viewer = document.createElement('model-viewer');
      entry.viewer = viewer;
      const attributes = {
        class: 'wia-orbit__viewer',
        'camera-controls': '', 'disable-pan': '', 'disable-zoom': '', 'disable-tap': '',
        'touch-action': 'pan-y', loading: 'eager', reveal: 'auto',
        'interaction-prompt': 'none', 'min-camera-orbit': 'auto auto 30%', 'max-camera-orbit': 'auto auto 300%',
        'field-of-view': '30deg', 'tone-mapping': 'neutral',
        'environment-image': 'neutral', exposure: '1', 'shadow-intensity': '0',
        'rotation-per-second': `${(entry.index % 2 ? -1 : 1) * (3.25 + (entry.index % 4) * .85)}deg`,
        'auto-rotate-delay': '2500',
        'camera-target': trigger.dataset.cameraTarget || 'auto auto auto',
        alt: `${trigger.dataset.moduleTitle} AX22 module. Drag or use arrow keys to rotate. Escape leaves the model.`,
      };
      Object.entries(attributes).forEach(([name, value]) => viewer.setAttribute(name, value));
      // Keep its real dimensions during loading so its first frame is prepared
      // behind the poster, without a second render-size change on interaction.
      viewer.style.opacity = '0';
      viewer.style.pointerEvents = 'none';
      viewer.inert = true;
      this.applyModulePose(entry);
      const { signal } = this.controller;
      const fail = () => {
        if (entry.viewer !== viewer || signal.aborted) return;
        const restoreFocus = wrapper.contains(document.activeElement);
        if (entry.state === 'loading') this.modulesLoading--;
        clearTimeout(entry.timeout);
        entry.state = 'failed';
        wrapper.classList.remove('is-loading', 'is-ready');
        trigger.hidden = false;
        trigger.removeAttribute('aria-busy');
        viewer.removeAttribute('src');
        viewer.remove();
        entry.viewer = null;
        if (restoreFocus) trigger.focus({ preventScroll: true });
        if (this.activeModule === entry) this.moduleStatus.textContent = `The 3D view of ${trigger.dataset.moduleTitle} could not load. Select its preview to retry.`;
        this.pumpModules();
      };
      viewer.addEventListener('error', fail, { signal });
      viewer.addEventListener('load', () => {
        if (signal.aborted || entry.viewer !== viewer || entry.state !== 'loading') return;
        clearTimeout(entry.timeout);
        this.applyModulePose(entry);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (signal.aborted || entry.viewer !== viewer || entry.state !== 'loading') return;
          entry.state = 'ready';
          this.modulesLoading--;
          wrapper.classList.remove('is-loading');
          wrapper.classList.add('is-ready');
          trigger.removeAttribute('aria-busy');
          const moveFocus = document.activeElement === trigger;
          trigger.hidden = true;
          viewer.style.opacity = '';
          viewer.style.pointerEvents = '';
          viewer.inert = false;
          if (moveFocus) this.focusModule(entry);
          if (this.activeModule === entry) this.moduleStatus.textContent = `${trigger.dataset.moduleTitle}. Drag or use arrow keys to rotate.`;
          this.updateRotation();
          this.pumpModules();
        }));
      }, { once: true, signal });
      wrapper.appendChild(viewer);
      const source = new URL(trigger.dataset.modelUrl, document.baseURI);
      // model-viewer also caches failed loads. A user retry needs a fresh key.
      if (entry.retries) source.searchParams.set('wia_retry', entry.retries);
      viewer.setAttribute('src', source.href);
      entry.timeout = setTimeout(fail, 30000);
    }

    applyModulePose(entry) {
      const { theta, phi, radius } = entry.pose;
      entry.viewer?.setAttribute('camera-orbit', `${theta}deg ${phi}deg ${radius}`);
      entry.viewer?.jumpCameraToGoal?.();
    }

    selectModule(entry, keyboard = false) {
      if (this.activeModule !== entry) {
        this.releaseModule();
        this.activeModule = entry;
        entry.wrapper.classList.add('is-active');
      }
      if (entry.state === 'failed') {
        entry.retries = (entry.retries || 0) + 1;
        entry.state = 'queued';
        this.moduleQueue.unshift(entry);
      }
      this.prioritizeModule(entry);
      if (entry.state !== 'ready') this.moduleStatus.textContent = `Preparing ${entry.trigger.dataset.moduleTitle} in 3D.`;
      else if (keyboard) this.focusModule(entry);
      this.updateRotation();
    }

    focusModule(entry) {
      entry.viewer?.shadowRoot?.querySelector('.userInput')?.focus({ preventScroll: true });
    }

    updateRotation() {
      const animate = this.isConnected && !document.hidden && !this.rotationPaused && !this.boardDrag && !this.moduleDrag && !this.nativeDragging;
      this.viewer?.toggleAttribute('auto-rotate', Boolean(animate && this.boardVisible && this.classList.contains('is-board-ready') && !this.classList.contains('is-board-hover') && !this.boardFocused && parseFloat(this.viewer.getAttribute('rotation-per-second')) > 0));
      this.modules?.forEach((entry) => {
        entry.viewer?.toggleAttribute('auto-rotate', Boolean(animate && this.stageVisible && entry.state === 'ready' && !entry.hovered && !entry.focused));
      });
      if (this.rotationButton) {
        this.rotationButton.textContent = this.rotationPaused ? 'Play rotation' : 'Pause rotation';
        this.rotationButton.setAttribute('aria-label', this.rotationPaused ? 'Play automatic model rotation' : 'Pause automatic model rotation');
      }
    }

    releaseModule() {
      this.endModuleDrag();
      this.activeModule?.wrapper.classList.remove('is-active');
      this.activeModule = null;
    }

    endModuleDrag() {
      const drag = this.moduleDrag;
      this.moduleDrag = null;
      this.nativeDragging = false;
      if (drag) {
        drag.entry.wrapper.classList.remove('is-dragging');
        if (drag.entry.wrapper.hasPointerCapture(drag.id)) drag.entry.wrapper.releasePointerCapture(drag.id);
      }
      this.updateRotation();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.endBoardDrag();
      this.releaseModule();
      this.rotationObserver?.disconnect();
      clearTimeout(this.boardTimeout);
      this.modules?.forEach((entry) => {
        clearTimeout(entry.timeout);
        entry.viewer?.removeAttribute('src');
        entry.viewer?.remove();
        entry.trigger.hidden = false;
        entry.trigger.removeAttribute('aria-busy');
        entry.wrapper.classList.remove('is-loading', 'is-ready');
      });
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
